import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  TextField,
  IconButton,
  Chip,
  Skeleton,
} from '@mui/material';
import { Edit, Save, Cancel } from '@mui/icons-material';
import { GlassmorphicCard } from '../GlassmorphicCard/GlassmorphicCard';
import { useSnackbar } from '../../hooks/SnackbarProvider';
import { useAuth } from '../../hooks/AuthProvider';
import { isValidPhone } from '../../lib/utils';

interface ProfileEditCardProps {
  className?: string;
}

interface ProfileData {
  phone: string;
  first_name: string;
  last_name: string;
}

const ProfileEditCard: React.FC<ProfileEditCardProps> = ({ className }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { triggerSnackbar } = useSnackbar();
  const { userProfile, updateProfile, isLoading: isProfileLoading } = useAuth();

  // Initialize with empty data
  const [editData, setEditData] = useState<ProfileData>({
    phone: '',
    first_name: '',
    last_name: '',
  });

  // Update editData when userProfile changes
  React.useEffect(() => {
    if (userProfile) {
      const newProfileData: ProfileData = {
        phone: userProfile.person?.phone || '',
        first_name: userProfile.firstName || '',
        last_name: userProfile.lastName || '',
      };
      setEditData(newProfileData);
    }
  }, [userProfile]);

  // Get current profile data (for display when not editing)
  const profileData: ProfileData = React.useMemo(() => ({
    phone: userProfile?.person?.phone || '',
    first_name: userProfile?.firstName || '',
    last_name: userProfile?.lastName || '',
  }), [userProfile]);

  const handleEdit = () => {
    setEditData(profileData);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditData(profileData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Validate form data
    if (!editData.first_name.trim() || !editData.last_name.trim()) {
      triggerSnackbar('First name and last name are required', 3000, 'error');
      return;
    }

    if (editData.phone && !isValidPhone(editData.phone)) {
      triggerSnackbar('Please enter a valid phone number', 3000, 'error');
      return;
    }

    setIsLoading(true);

    try {
      // Update profile via AuthProvider
      await updateProfile({
        first_name: editData.first_name,
        last_name: editData.last_name,
        phone: editData.phone,
      } as any);

      setIsEditing(false);
      triggerSnackbar('Profile updated successfully!', 3000, 'success');
    } catch (error: any) {
      console.error('Profile update error:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to update profile. Please try again.';
      triggerSnackbar(errorMessage, 3000, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProfileData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  // Show loading skeleton when profile is loading
  if (isProfileLoading && !userProfile) {
    return (
      <GlassmorphicCard className={`profile-edit-card ${className || ''}`}>
        <Box p={3}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Skeleton variant="text" width={120} height={32} />
            <Skeleton variant="circular" width={40} height={40} />
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={2}>
              <Skeleton variant="rectangular" width="100%" height={56} />
              <Skeleton variant="rectangular" width="100%" height={56} />
            </Box>
            <Skeleton variant="rectangular" width="100%" height={56} />
            <Skeleton variant="rectangular" width="100%" height={56} />
          </Box>
        </Box>
      </GlassmorphicCard>
    );
  }

  return (
    <GlassmorphicCard className={`profile-edit-card ${className || ''}`}>
      <Box p={3}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Edit Profile
          </Typography>

          {!isEditing ? (
            <IconButton
              onClick={handleEdit}
              sx={{
                background: 'linear-gradient(135deg, #f06ef5, #9c88ff)',
                color: 'white',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 20px rgba(240, 110, 245, 0.4)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          ) : (
            <Box display="flex" gap={1}>
              <IconButton
                onClick={handleCancel}
                disabled={isLoading}
                color="error"
                size="small"
              >
                <Cancel fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleSave}
                disabled={isLoading}
                sx={{
                  background: 'linear-gradient(135deg, #9be15d, #00e3ae)',
                  color: 'white',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                  '&:disabled': {
                    background: '#ccc',
                    color: '#666',
                  },
                }}
                size="small"
              >
                <Save fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Form Fields */}
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={2}>
            <TextField
              label="First Name"
              value={isEditing ? editData.first_name : profileData.first_name}
              onChange={handleInputChange('first_name')}
              disabled={!isEditing || isLoading}
              fullWidth
              InputProps={{
                readOnly: !isEditing,
              }}
              sx={{
                '& .MuiInputBase-input': {
                  color: isEditing ? 'inherit' : 'text.secondary',
                },
              }}
            />

            <TextField
              label="Last Name"
              value={isEditing ? editData.last_name : profileData.last_name}
              onChange={handleInputChange('last_name')}
              disabled={!isEditing || isLoading}
              fullWidth
              InputProps={{
                readOnly: !isEditing,
              }}
              sx={{
                '& .MuiInputBase-input': {
                  color: isEditing ? 'inherit' : 'text.secondary',
                },
              }}
            />
          </Box>

          <TextField
            label="Phone Number"
            value={isEditing ? editData.phone : profileData.phone}
            onChange={handleInputChange('phone')}
            disabled={!isEditing || isLoading}
            fullWidth
            InputProps={{
              readOnly: !isEditing,
            }}
            sx={{
              '& .MuiInputBase-input': {
                color: isEditing ? 'inherit' : 'text.secondary',
              },
            }}
          />
        </Box>

        {/* Status */}
        {!isEditing && (
          <Box mt={2}>
            <Chip
              label="Profile Complete"
              color="success"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #9be15d, #00e3ae)',
                color: 'white',
              }}
            />
          </Box>
        )}
      </Box>
    </GlassmorphicCard>
  );
};

export default ProfileEditCard;